import { ApiError } from '@thyme/libs/src/api/apiRequest'
import { Method } from '@thyme/libs/src/api/types/api'
import { Store, StoreDefinition } from 'pinia'
import { GenericListResponse } from '@/libs/api/types'
import { SaveState } from './api'
import { IdMap } from './store'

// INTERFACES --------------------

export interface ApiStoreState<Datum, Data> {
  data: Data | null
  datum: Datum | null
  isLoading: boolean
  isLoaded: boolean
  error: ApiError | Error | null
  queryMetadata: { total: number } | null
  idMap: IdMap<Datum> | null
}

interface ComputedStoreData<Datum, Data> {
  computedData: (state?: ApiStoreState<Datum, Data>) => Data | null
}

// TYPES --------------------
type ApiStoreMutations<Datum, Data, SingularResp, ListResp> = {
  isQueued(): boolean
  setSuccess: (
    data: SingularResp | ListResp | null,
    type: DataType,
    saveState?: SaveState
  ) => void
  setPending(saveState?: SaveState): void
  setError(error: ApiError | Error, saveState?: SaveState): void
  // transformations
  transform(
    data: SingularResp | ListResp | null,
    type: DataType
  ): ApiStoreState<Datum, Data>
  transformData(data: ListResp | null): ApiStoreState<Datum, Data>
  transformDatum(datum: SingularResp | null): ApiStoreState<Datum, Data>
  // data updates
  reset(): void
  setData(data: Datum | Data): void
  updateData(newData: SingularResp | ListResp | null, type: DataType): void
  updateInPlace({ id, item }: { id: string; item: Datum }): Promise<void>
  // core calls
  splitApiActionCall(
    method: Method,
    url: string,
    options: ApiActionOptions
  ): Promise<{ data: any[]; queryMetadata: { total: number } }>
  apiAction(
    method: Method,
    url: string,
    dataType: DataType,
    options: ApiActionOptions,
    setup?: SetupOptions
  ): Promise<SingularResp | ListResp | null>
}

type ApiActions<SingularResp, ListResp> = {
  list<H extends Record<string, any>, P extends Record<string, any>>(options: {
    headers?: H
    params?: P
    ids?: string | string[] | null
    metaOptions?: PassedSetupOptions
  }): Promise<ListResp>
  listAll<
    H extends Record<string, any>,
    P extends Record<string, any>,
  >(options: {
    headers?: H
    params?: P
    ids?: string | string[] | null
    metaOptions?: PassedSetupOptions
  }): Promise<ListResp>
  retrieve<
    H extends Record<string, any>,
    P extends Record<string, any>,
  >(options: {
    ids: string | string[]
    headers?: H
    params?: P
    metaOptions?: PassedSetupOptions
  }): Promise<SingularResp>
  create<
    H extends Record<string, any>,
    B extends Record<string, any>,
    P extends Record<string, any>,
  >(options: {
    headers?: H
    params?: P
    body?: B
    ids?: string | string[] | null
    metaOptions?: PassedSetupOptions
    file?: File | null
  }): Promise<SingularResp>
  upsert<
    H extends Record<string, any>,
    B extends Record<string, any>,
  >(options: {
    headers?: H
    body?: B
    ids?: string | string[] | null
    metaOptions?: PassedSetupOptions
  }): Promise<SingularResp>
  update<
    H extends Record<string, any>,
    B extends Record<string, any>,
  >(options: {
    headers?: H
    body?: B
    ids?: string | string[] | null
    metaOptions?: PassedSetupOptions
  }): Promise<SingularResp>
  partialUpdate<
    H extends Record<string, any>,
    B extends Record<string, any>,
    P extends Record<string, any>,
  >(options: {
    headers?: H
    body?: B
    params?: P
    ids?: string | string[] | null
    extraHeaders?: { [key: string]: string }
    metaOptions?: PassedSetupOptions
  }): Promise<SingularResp>
  delete<
    H extends Record<string, any>,
    P extends Record<string, any>,
  >(options: {
    ids?: string | string[] | null
    headers?: H
    metaOptions?: PassedSetupOptions
    params?: P
  }): Promise<void>
}

type ApiStoreFunctions<Datum, Data, SingularResp, ListResp> = ApiStoreMutations<
  Datum,
  Data,
  SingularResp,
  ListResp
> &
  ApiActions<SingularResp, ListResp>

export type ApiStoreDefinition<
  Datum,
  Data = Datum[],
  SingularResp = Datum,
  ListResp = GenericListResponse<SingularResp>,
> = StoreDefinition<
  string,
  ApiStoreState<Datum, Data>,
  ComputedStoreData<Datum, Data>,
  ApiStoreFunctions<Datum, Data, SingularResp, ListResp>
>

export type ApiActionOptions = {
  headers?: { [key: string]: any }
  params?: { [key: string]: any }
  body?: { [key: string]: any }
  file?: File | null
}

export type SetupOptions = {
  fetchAll?: boolean
  /**
   * saveState: tracker for save status
   */
  saveState?: SaveState
  /**
   *  bubbleErrorThrow: true -> bubble error for try/catch workflows
   *  WARNING WILL CAUSE SERIOUS PAGE BREAKAGE IF NOT CAUGHT
   */
  bubbleErrorThrow?: boolean

  /**
   * maxListAllPagesAllowed: maximum amount of pages to fetch in ListAll command before throwing an error
   * This is a sanity check to prevent accidentally fetching thousands of pages in case of bad filters
   * Only applicable if fetchAll is true
   * Default is set to MAX_PAGE_COUNT_SANITY_CHECK, but can be overridden in case of legitimately large datasets (with caution)
   */
  maxListAllPagesAllowed?: number
}

export type PassedSetupOptions = Omit<SetupOptions, 'fetchAll'>

export type ApiStoreOptions<
  Datum,
  Data = Datum[],
  SingularResp = Datum,
  ListResp = GenericListResponse<SingularResp>,
> = {
  transformData?: (
    data: ListResp,
    state?: ApiStoreState<Datum, Data>
  ) => Partial<ApiStoreState<Datum, Data>> & { data: Data | null | undefined }
  transformDatum?: (
    datum: SingularResp,
    state?: ApiStoreState<Datum, Data>
  ) => Partial<ApiStoreState<Datum, Data>> & { datum: Datum | null | undefined }
  headers?: { [key: string]: unknown }
  params?: { [key: string]: unknown }
}

// ENUMS --------------------
/** Determines where to store returned data/datum from apiResponse */
export enum DataType {
  SINGULAR = 'singular',
  PLURAL = 'plural',
  PATCH = 'patch',
}

// CONSTANTS --------------------
/**
 * Maximum amount of items to grab from a single API call
 *  Used by ListAll command to split api calls into smaller chunks
 */
export const MAX_PAGE_SIZE = 100

// Maximum amount of pages to grab before throwing an error in ListAll command
// This is a sanity check to prevent accidentally fetching thousands of pages in case of bad filters
export const MAX_PAGE_COUNT_SANITY_CHECK = 10

export const initialApiState = () => ({
  isLoading: false,
  error: null,
})

// Legacy result tracking code
export interface ApiResultTrackingPartial {
  isLoading: boolean
  error: ApiError | Error | null
}

// Pinia uses 'this' instead of 'state' parameter, so we need some typings to
// ensure API mutations can access the right properties on 'this'.
type ApiResultTracking<S extends ApiResultTrackingPartial, D> = {
  loading: () => void
  success: (data: D) => void
  setError: (error: ApiError | Error) => void
} & ThisType<Store<'', S>>

export const apiResultTracking = <S extends ApiResultTrackingPartial, T>(
  transform: (data: T) => Partial<S>
): ApiResultTracking<S, T> => ({
  loading() {
    this.isLoading = true
  },
  success(data: T) {
    Object.assign(this.$state, transform(data))
    this.isLoading = false
    this.error = null
  },
  setError(error: ApiError | Error) {
    this.error = error
    this.isLoading = false
  },
})
